import { Button, cn } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";
import { IcChevronDown } from "@racoon/icons";
import { STATUS_TONE_CLASS, twoGisLinkOf } from "@racoon/lib";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { format, isToday } from "date-fns";
import { useGetPickupLocationByIdQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { Row } from "./row";
import { PickupLocationModal } from "~/features/pickup-location-modal/ui/pickup-location.modal";
import { formatPickupLocationToForm } from "~/pages/dashboard/pickup-locations/model/formatPickupLocationToForm";
import { useInfoPanel } from "../model/useInfoPanel";
import { jobsPageUrl } from "~/pages/dashboard/orders/model/jobs-page-url";
import { useOpenPhotoReport } from "~/features/photo-report-modal/ui/photo-report.modal";
import { useDashboardDay } from "~/shared/lib/pickup-status/dashboard-day";
import { useSiteDayJobs } from "~/shared/lib/pickup-status/use-pickup-status";
import { isSetId } from "~/shared/lib/pickup-status/pickup-status.lib";
import { useJobLabel } from "~/shared/lib/pickup-status/use-job-label";
import type { SiteJob } from "~/shared/lib/pickup-status/site-pins";
import { pinSvg } from "~/features/map-view/model/constants/site-pin";

type Props = {
  id: string;
  setHeader?: (element: React.ReactNode) => void;
};

// Figma «Dashboard_PickupLocation Selected»: what the site is and its jobs of the shown day.
export function PickupLocationInfoCard({ id, setHeader }: Props) {
  const t = useT();
  // From the URL, like the dashboard: the stored selection is empty on a direct link.
  const { activeCompanyId: selectedCompanyId } = useAuthSession();
  // The panel opens on every page; only the dashboard has a day to pick.
  const onDashboard = /\/dashboard\/?$/.test(useLocation().pathname);
  const dashboardDay = useDashboardDay();
  const day = onDashboard ? dashboardDay : new Date();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const isOwnForWrite = useIsOwnOrganizationForWrite();

  // By id: the provider's site list leaves out sites its divisions serve.
  // currentData: data would show the previous site while the next one loads.
  const { currentData: location, isFetching } = useGetPickupLocationByIdQuery(
    /^[1-9]\d*$/.test(id) && selectedCompanyId
      ? { id: BigInt(id), serviceProviderId: BigInt(selectedCompanyId) }
      : skipToken,
  );

  // The by-id call leaves container groups out.
  const { currentData: groupList, isError: groupsError } = useListContainerGroupsQuery(
    /^[1-9]\d*$/.test(id) ? { pickupLocationIds: [BigInt(id)] } : skipToken,
  );
  const containerGroups = useMemo(() => groupList?.containerGroups ?? [], [groupList]);

  const {
    jobs,
    isLoading: jobsLoading,
    isError: jobsError,
  } = useSiteDayJobs(id, day, selectedCompanyId ?? null);

  // Not before the groups arrive: the form would save a count of 0 over the real one.
  const modalLocation = useMemo(
    () =>
      location && groupList
        ? {
            ...formatPickupLocationToForm({ ...location, containerGroups }),
            id: Number(location.id),
          }
        : undefined,
    [location, groupList, containerGroups],
  );

  const tone = jobs[0]?.tone;
  useEffect(() => {
    if (!setHeader) return;
    // Cleared while the next site loads, or the old site's name would label it.
    if (!location) {
      setHeader(null);
      return;
    }
    setHeader(
      <span className="flex items-center gap-2 font-semibold text-text-primary">
        <span
          className="shrink-0"
          dangerouslySetInnerHTML={{ __html: pinSvg(tone ?? "active", 18, 22) }}
        />
        <span>{location.name}</span>
      </span>,
    );
  }, [location, setHeader, tone]);

  if (!location && isFetching) {
    return <div className="p-4">{t(Namespace.Common, "loading")}</div>;
  }
  if (!location) {
    return <div className="p-4 text-sm text-text-secondary">{t(Namespace.Common, "noData")}</div>;
  }

  const gisLink = twoGisLinkOf(location);
  const dayTitle = isToday(day)
    ? t(Namespace.Infopanel, "siteJobs.today")
    : t(Namespace.Infopanel, "siteJobs.onDay", { date: format(day, "dd.MM") });

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 border-b p-4">
        <Row label={t(Namespace.Infopanel, "labels.siteType")}>
          {location.pickupLocationType?.name || t(Namespace.Infopanel, "empty")}
        </Row>
        <Row label={t(Namespace.Infopanel, "labels.geolocation")}>
          {gisLink ? (
            <a
              href={gisLink}
              target="_blank"
              rel="noreferrer"
              className="!text-text-primary underline"
            >
              {t(Namespace.Infopanel, "labels.gisLink")}
            </a>
          ) : (
            t(Namespace.Infopanel, "empty")
          )}
        </Row>
        {containerGroups.map((group, index) => (
          <Row key={group.id} label={`${t(Namespace.Infopanel, "labels.contGroup")} ${index + 1}`}>
            {group.containerType?.name} ({group.numberOfContainers})
          </Row>
        ))}
      </div>

      <div className="flex-1 space-y-3 p-4">
        <h3 className="text-xs font-semibold uppercase text-text-primary">{dayTitle}</h3>
        {jobsLoading ? (
          <div className="text-sm text-text-secondary">{t(Namespace.Common, "loading")}</div>
        ) : jobsError ? (
          <div className="text-sm text-text-error">
            {t(Namespace.Infopanel, "siteJobs.loadError")}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-sm text-text-secondary">
            {t(Namespace.Infopanel, "siteJobs.none")}
          </div>
        ) : (
          jobs.map((entry) => (
            <SiteJobCard
              key={String(entry.job.id)}
              entry={entry}
              companyId={selectedCompanyId}
              allJobs={jobs}
            />
          ))
        )}
      </div>

      <div className="mt-auto p-4">
        {groupsError && (
          <div className="mb-2 text-sm text-text-error">
            {t(Namespace.Infopanel, "siteJobs.groupsLoadError")}
          </div>
        )}
        <Button
          id="pickup-info-open-location-button"
          className="w-full"
          variant="opacityBlueWithoutOutline"
          disabled={!modalLocation}
          onClick={() => setIsLocationModalOpen(true)}
        >
          {t(Namespace.Infopanel, "actions.objectCard")}
        </Button>
      </div>

      <PickupLocationModal
        open={isLocationModalOpen}
        onOpenChange={setIsLocationModalOpen}
        hideTrigger
        location={modalLocation}
        readOnly={isOwnForWrite(location.ownerOrganizationId, location.serviceProviderId) !== true}
      />
    </div>
  );
}

function SiteJobCard({
  entry,
  companyId,
  allJobs,
}: {
  entry: SiteJob;
  companyId: number | null | undefined;
  allJobs: SiteJob[];
}) {
  const t = useT();
  const navigate = useNavigate();
  const { open } = useInfoPanel();
  const openPhotoReport = useOpenPhotoReport();
  const jobLabel = useJobLabel();
  const [expanded, setExpanded] = useState(true);
  const { job, tone, bucket } = entry;
  const group = job.containerGroup;
  // Only a visited site has a report: done, or marked as not collected.
  const hasReport = bucket === "done" || job.status === ConnectProto.JobStatus.NOT_COMPLETED;

  const jobUrl = jobsPageUrl(companyId ?? "", job);

  // The whole card opens the job for the mouse; the «Z…» link is its keyboard / reader target,
  // so the card itself holds no interactive role around the buttons inside it.
  return (
    <div
      title={t(Namespace.Infopanel, "siteJobs.openInJobs")}
      onClick={() => navigate(jobUrl)}
      className="cursor-pointer space-y-2 rounded-lg bg-slate-100 p-3 transition-colors hover:bg-slate-200/70"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-text-primary">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t(Namespace.Infopanel, "siteJobs.toggle")}
            className="-ml-1 rounded p-0.5 text-brand-blue hover:bg-white"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            <IcChevronDown
              className={cn("h-4 w-4 transition-transform", expanded ? "rotate-180" : "rotate-90")}
            />
          </button>
          <Link
            to={jobUrl}
            onClick={(e) => e.stopPropagation()}
            className="rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          >
            Z{String(job.id)}
          </Link>
        </span>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-semibold",
            STATUS_TONE_CLASS[tone].bg,
            STATUS_TONE_CLASS[tone].color,
          )}
        >
          {jobLabel(entry)}
        </span>
      </div>
      {expanded && (
        <>
          <Row label={t(Namespace.Infopanel, "labels.contGroup")}>
            {group?.containerType
              ? `${group.containerType.name} (${group.numberOfContainers})`
              : t(Namespace.Infopanel, "empty")}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.executor")}>
            {job.serviceProvider?.name ?? t(Namespace.Infopanel, "empty")}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.route")}>
            {isSetId(job.routeId) ? (
              <button
                type="button"
                className="text-brand-blue hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  open("route", String(job.routeId), { fromCard: true });
                }}
              >
                M{String(job.routeId)}
              </button>
            ) : (
              t(Namespace.Infopanel, "empty")
            )}
          </Row>
          {hasReport && (
            <button
              type="button"
              className="w-full pt-1 text-center text-sm font-medium text-brand-blue hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                openPhotoReport(
                  job,
                  allJobs.map((j) => j.job),
                );
              }}
            >
              {t(Namespace.PhotoReport, "photoReport")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
